// indexOptimized.jsx
// Fully integrated and optimized Device Management component
// Part of Device Management Overhaul - Final Integration

import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  AccountTree as TreeIcon,
  Hub as NetworkIcon,
  ViewList as ListIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Core components
import { useIsMobile } from '../../hooks/useResponsive';
import { usePermissions } from '../../contexts/PermissionContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { useSiteFilter } from '../../contexts/SiteFilterContext';
import DeviceService from '../../services/deviceService';
import { devicesApi, locationsApi } from '../../services/apiClient';

// Optimized components
import OptimizedDeviceList from './components/DeviceList/OptimizedDeviceList';
import StatusIndicator from './components/StatusIndicators/StatusIndicator';
import AlertBanner from './components/Alerts/AlertBanner';
import BulkActionToolbar from './components/BulkActions/BulkActionToolbar';
import FilterPanel from './components/FilterPanel';
import UnifiedSearch from './components/SearchBar/UnifiedSearch';

// Performance hooks
import useFilteredDevices from './hooks/useFilteredDevices';

// Lazy load heavy components
const DeviceTreeView = lazy(() => import('../../components/DeviceTree/DeviceTreeView'));
const EnhancedNetworkView = lazy(() => import('./components/NetworkView/EnhancedNetworkView'));
const DeviceDialog = lazy(() => import('../../components/DeviceDialog'));
const ConfirmDialog = lazy(() => import('../../components/ConfirmDialog'));
const DeviceDetailsPanel = lazy(() => import('./components/DeviceDetails/DeviceDetailsPanel'));

// Loading component
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
    <CircularProgress />
  </Box>
);

const DeviceManagementOptimized = () => {
  const { t } = useThemeSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobile();
  const { selectedSiteId, siteLocationIds, hasSiteFilter } = useSiteFilter();

  // State management
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true); // Only for first load
  const [refreshing, setRefreshing] = useState(false); // For user-triggered refresh
  const [devicesError, setDevicesError] = useState(null);
  const [cacheAge, setCacheAge] = useState(null); // Show cache age to user
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [deviceToEdit, setDeviceToEdit] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);

  // Performance monitoring - use ref to avoid re-renders on metrics update
  const performanceMetricsRef = useRef({
    loadTime: 0,
    filterTime: 0,
    renderTime: 0
  });

  // Stable callback for search - debounced in UnifiedSearch, so this just needs to be stable
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  // Use optimized filtering hook
  const {
    filteredDevices,
    performanceMetrics: filterMetrics,
    clearCache,
    sortDevices
  } = useFilteredDevices(devices, filters, searchTerm);

  // Sort filtered devices
  const sortedDevices = useMemo(() => {
    return sortDevices(filteredDevices, sortBy, sortOrder);
  }, [filteredDevices, sortBy, sortOrder, sortDevices]);

  // Memoized device icons - stable reference
  const getDeviceIcon = useCallback((deviceTypeModel) => {
    // Return a simple icon placeholder - actual icon rendering can be enhanced
    // Using null triggers fallback rendering in DeviceRow
    return null;
  }, []);

  // Load devices with performance tracking
  const loadDevices = useCallback(async (forceRefresh = false, isInitial = false, backgroundRefresh = false) => {
    const startTime = performance.now();

    try {
      // Skip cache-first strategy to avoid complexity with site filtering
      // Always fetch fresh data - it's simpler and more reliable

      // Show loading indicators only for non-background operations
      if (!backgroundRefresh) {
        if (isInitial) {
          setInitialLoading(true);
        } else if (forceRefresh) {
          setRefreshing(true);
        }
      }

      setDevicesError(null);

      const response = await DeviceService.getAllDevices({
        forceRefresh: true, // Always force refresh when actually fetching
        useCache: false,
        enhanced: true
      });

      let deviceList = [];
      // Robust data extraction with multiple fallback paths
      if (response?.data?.data) {
        deviceList = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (response?.data?.devices) {
        deviceList = Array.isArray(response.data.devices) ? response.data.devices : [];
      } else if (response?.data) {
        deviceList = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        deviceList = response;
      } else {
        console.error('[DeviceManagement] Could not extract devices from response structure');
      }

      // Additional validation
      if (!Array.isArray(deviceList)) {
        console.error('[DeviceManagement] Expected array but got:', typeof deviceList);
        deviceList = [];
      }

      // Filter by site if a site filter is active
      // Filter by site - when hasSiteFilter is true and siteLocationIds is set (even if empty array for no-access users)
      if (hasSiteFilter && siteLocationIds) {
        deviceList = deviceList.filter(device => {
          // Check all possible location ID fields
          const deviceLocationId = device.locationId || device.location_id || device.location?.id || device.deviceLocation || device.locationModel?.id;
          if (!deviceLocationId) {
            // Devices without location should not be shown when site filter is active
            return false;
          }
          return siteLocationIds.includes(Number(deviceLocationId)) || siteLocationIds.includes(String(deviceLocationId));
        });
        console.log(`[DeviceManagement] Filtered to ${deviceList.length} devices for selected site (from siteLocationIds: ${siteLocationIds.length})`);
      }

      setDevices(deviceList);

      // Update cache age
      setCacheAge(0); // Just refreshed

      // Track performance - use ref to avoid re-render
      const loadTime = performance.now() - startTime;
      performanceMetricsRef.current.loadTime = loadTime;

      // Start status polling ONLY on initial load
      if (isInitial && !backgroundRefresh) {
        DeviceService.startStatusPolling((statuses) => {
          // SILENTLY update device statuses without triggering loading state
          setDevices(prev => prev.map(device => ({
            ...device,
            status: statuses[device.id] || device.status
          })));
        }, 30000);
      }

      // Show success message for background refresh
      if (backgroundRefresh && deviceList.length > 0) {
        enqueueSnackbar(`Refreshed ${deviceList.length} devices`, { variant: 'success', autoHideDuration: 2000 });
      }

    } catch (error) {
      console.error('Error loading devices:', error);

      if (!backgroundRefresh) {
        setDevicesError(error.message || 'Failed to load devices');
        setDevices([]);
      } else {
        // Silent failure for background refresh
        console.warn('[DeviceManagement] Background refresh failed, keeping cached data');
      }
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, [enqueueSnackbar, hasSiteFilter, siteLocationIds]);

  // Load locations
  const loadLocations = useCallback(async () => {
    try {
      const response = await locationsApi.getAll();
      setLocations(response.data?.data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  }, []);

  // Track previous site selection to detect changes
  const prevSiteIdRef = useRef(selectedSiteId);

  // Initial load - also re-fetch when site filter changes
  useEffect(() => {
    // Wait for siteLocationIds to be ready if site filter is active
    // siteLocationIds will be null while loading, then an array when ready
    if (hasSiteFilter && !Array.isArray(siteLocationIds)) {
      console.log('[DeviceManagement] Site filter active but siteLocationIds not loaded yet, waiting...');
      setInitialLoading(true);
      return; // Will re-trigger when siteLocationIds becomes available
    }

    // Detect if site selection actually changed
    const siteChanged = prevSiteIdRef.current !== selectedSiteId;
    prevSiteIdRef.current = selectedSiteId;

    // ALWAYS invalidate cache when site selection changes (including to/from 'all')
    // This prevents showing stale data from a different site selection
    if (siteChanged) {
      console.log('[DeviceManagement] Site changed, invalidating cache');
      DeviceService.invalidateCache();
    }

    // Force refresh when site changed or when site filter is active
    const forceRefresh = siteChanged || hasSiteFilter;
    loadDevices(forceRefresh, true);
    loadLocations();

    return () => {
      DeviceService.stopStatusPolling();
    };
  // Note: loadDevices and loadLocations are stable useCallback functions, safe to exclude
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteId, siteLocationIds, hasSiteFilter]);

  // Handle URL parameters
  useEffect(() => {
    const selectedId = searchParams.get('selectedId');
    if (selectedId && devices.length > 0) {
      const device = devices.find(d => String(d.id) === String(selectedId));
      if (device) {
        setSelectedDevice(device);
      }
    }
  }, [searchParams, devices]);

  // Device selection handlers
  const handleDeviceSelect = useCallback((device) => {
    setSelectedDevice(device);
    const deviceId = device.id || device._id;
    if (deviceId) {
      setSearchParams({ selectedId: deviceId });
    }
  }, [setSearchParams]);

  const handleBulkSelect = useCallback((deviceId) => {
    setSelectedDevices(prev => {
      if (prev.includes(deviceId)) {
        return prev.filter(id => id !== deviceId);
      }
      return [...prev, deviceId];
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedDevices(sortedDevices.map(d => d.id));
  }, [sortedDevices]);

  const handleDeselectAll = useCallback(() => {
    setSelectedDevices([]);
  }, []);

  // Device action handlers
  const handleDeviceEdit = useCallback((device) => {
    setDeviceToEdit(device);
    setDialogMode('edit');
    setDialogOpen(true);
  }, []);

  const handleDeviceDelete = useCallback((device) => {
    setDeviceToDelete(device);
    setConfirmDialogOpen(true);
  }, []);

  const confirmDeleteDevice = useCallback(async () => {
    if (!deviceToDelete) return;

    try {
      const deviceId = deviceToDelete.id || deviceToDelete._id;
      await devicesApi.delete(deviceId);

      DeviceService.invalidateCache();
      loadDevices(true);

      setConfirmDialogOpen(false);
      setDeviceToDelete(null);

      if (selectedDevice?.id === deviceToDelete.id) {
        setSelectedDevice(null);
      }

      enqueueSnackbar('Device deleted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting device:', error);
      enqueueSnackbar('Failed to delete device', { variant: 'error' });
    }
  }, [deviceToDelete, selectedDevice, loadDevices, enqueueSnackbar]);

  // Bulk action handler
  const handleBulkAction = useCallback(async (actionData) => {
    try {
      const response = await devicesApi.bulkAction(actionData);

      if (response.data.success) {
        DeviceService.invalidateCache();
        loadDevices(true);
        return { success: true };
      }

      return { success: false, error: response.data.error };
    } catch (error) {
      console.error('Bulk action error:', error);
      return { success: false, error: error.message };
    }
  }, [loadDevices]);

  // Filter handlers
  const handleFilterOffline = useCallback(() => {
    setFilters(prev => ({ ...prev, status: 'offline' }));
  }, []);

  const handleFilterUnlinked = useCallback(() => {
    setFilters(prev => ({ ...prev, connection: 'unlinked' }));
  }, []);

  const handleRefresh = useCallback(() => {
    clearCache();
    loadDevices(true);
  }, [clearCache, loadDevices]);

  // Performance report
  useEffect(() => {
    if (filterMetrics.computationTime > 200) {
      console.warn(`[Performance] Filter computation took ${filterMetrics.computationTime}ms`);
    }
  }, [filterMetrics]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {t ? t('DeviceManagement.device_management') : 'Device Management'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sortedDevices.length} of {devices.length} devices shown
              {filterMetrics.computationTime > 0 && (
                <> • Filtered in {filterMetrics.computationTime.toFixed(0)}ms</>
              )}
              {cacheAge !== null && (
                <> • Cache age: {cacheAge < 60 ? `${cacheAge}s` : `${Math.round(cacheAge / 60)}m`}</>
              )}
            </Typography>
          </Box>
          {cacheAge !== null && cacheAge > 60 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => loadDevices(true)}
              sx={{ ml: 2 }}
            >
              Refresh Data
            </Button>
          )}
        </Box>
      </Box>

      {/* Alert Banner */}
      <AlertBanner
        devices={sortedDevices}
        onRefresh={handleRefresh}
        onFilterOffline={handleFilterOffline}
        onFilterUnlinked={handleFilterUnlinked}
        autoRefresh={true}
        refreshInterval={30000}
      />

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedDevices={selectedDevices}
        totalDevices={sortedDevices.length}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
        onBulkAction={handleBulkAction}
        locations={locations}
        tags={['Critical', 'Maintenance', 'Production', 'Testing']}
        visible={selectedDevices.length > 0}
      />

      {/* Controls Bar */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        {/* Top row: Search, Quick Filters, Actions */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={5}>
            <UnifiedSearch
              onSearch={handleSearchChange}
              totalDevices={devices.length}
              filteredCount={sortedDevices.length}
              isLoading={initialLoading}
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {/* Quick Filter Buttons - More Prominent */}
              <Button
                variant={filters.status === 'offline' ? 'contained' : 'outlined'}
                color={filters.status === 'offline' ? 'warning' : 'inherit'}
                size="small"
                onClick={() => {
                  setFilters(prev => ({
                    ...prev,
                    status: prev.status === 'offline' ? undefined : 'offline'
                  }));
                }}
              >
                Offline Only
              </Button>

              <Button
                variant={filters.status === 'online' ? 'contained' : 'outlined'}
                color={filters.status === 'online' ? 'success' : 'inherit'}
                size="small"
                onClick={() => {
                  setFilters(prev => ({
                    ...prev,
                    status: prev.status === 'online' ? undefined : 'online'
                  }));
                }}
              >
                Online Only
              </Button>

              <Button
                variant={filters.unlinked ? 'contained' : 'outlined'}
                color={filters.unlinked ? 'error' : 'inherit'}
                size="small"
                onClick={() => {
                  setFilters(prev => ({
                    ...prev,
                    unlinked: !prev.unlinked
                  }));
                }}
              >
                Unlinked
              </Button>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, mode) => mode && setViewMode(mode)}
                size="small"
              >
                <ToggleButton value="list">
                  <ListIcon />
                </ToggleButton>
                <ToggleButton value="tree">
                  <TreeIcon />
                </ToggleButton>
                <ToggleButton value="network">
                  <NetworkIcon />
                </ToggleButton>
              </ToggleButtonGroup>

              {hasPermission('devices.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setDeviceToEdit(null);
                    setDialogMode('create');
                    setDialogOpen(true);
                  }}
                  size="small"
                >
                  Add Device
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Second row: Active Filters + Advanced Filters (collapsed by default) */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Show active filter count */}
              {Object.keys(filters).length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Active filters: {Object.keys(filters).length}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setFilters({})}
                    sx={{ minWidth: 'auto' }}
                  >
                    Clear All
                  </Button>
                </>
              )}

              {/* Advanced Filters - Collapsible */}
              <FilterPanel
                filters={filters}
                onFilterChange={setFilters}
                locations={locations}
                deviceTypes={(() => {
                  // Extract unique device types with id and friendlyName
                  const typeMap = new Map();
                  devices.forEach(d => {
                    if (d.deviceTypeModel?.id) {
                      typeMap.set(d.deviceTypeModel.id, {
                        id: d.deviceTypeModel.id,
                        friendlyName: d.deviceTypeModel.friendlyName || d.deviceTypeModel.name || `Type ${d.deviceTypeModel.id}`
                      });
                    }
                  });
                  return Array.from(typeMap.values());
                })()}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto', position: 'relative', minHeight: 0 }}>
        {/* Show refreshing indicator for user-triggered refresh */}
        {refreshing && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'background.paper',
              px: 2,
              py: 1,
              borderRadius: 1,
              boxShadow: 2
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="body2">Refreshing...</Typography>
          </Box>
        )}

        {initialLoading ? (
          <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={100} />
          </Box>
        ) : devicesError ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">Failed to load devices: {devicesError}</Typography>
            <Button onClick={handleRefresh} sx={{ mt: 2 }}>
              Retry
            </Button>
          </Box>
        ) : devices.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No devices found</Typography>
            {hasPermission('devices.create') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setDeviceToEdit(null);
                  setDialogMode('create');
                  setDialogOpen(true);
                }}
                sx={{ mt: 2 }}
              >
                Add Your First Device
              </Button>
            )}
          </Box>
        ) : (
          <Grid container sx={{ height: '100%', minHeight: 'calc(100vh - 400px)' }}>
            {/* Device List/Tree/Network View */}
            <Grid item xs={12} md={selectedDevice ? 7 : 12} sx={{ height: '100%', minHeight: 600 }}>
              <Box sx={{ height: '100%', p: 2, overflow: 'auto' }}>
                {viewMode === 'list' && (
                  <OptimizedDeviceList
                    devices={sortedDevices}
                    selectedDevices={selectedDevices}
                    onDeviceSelect={handleBulkSelect}
                    onDeviceEdit={handleDeviceEdit}
                    onDeviceDelete={handleDeviceDelete}
                    onDeviceNavigate={handleDeviceSelect}
                    getDeviceIcon={getDeviceIcon}
                    hasEditPermission={hasPermission('devices.edit')}
                    hasDeletePermission={hasPermission('devices.delete')}
                    showCheckbox={true}
                  />
                )}

                {viewMode === 'tree' && (
                  <Suspense fallback={<LoadingFallback />}>
                    <DeviceTreeView
                      devices={devices}
                      searchTerm={searchTerm}
                      selectedDeviceId={selectedDevice?.id}
                      onDeviceSelect={handleDeviceSelect}
                      onDeviceEdit={handleDeviceEdit}
                      onDeviceDelete={handleDeviceDelete}
                      getDeviceIcon={getDeviceIcon}
                    />
                  </Suspense>
                )}

                {viewMode === 'network' && (
                  <Suspense fallback={<LoadingFallback />}>
                    <EnhancedNetworkView
                      devices={sortedDevices}
                      selectedDeviceId={selectedDevice?.id}
                      onDeviceSelect={handleDeviceSelect}
                      onDeviceEdit={handleDeviceEdit}
                      onDeviceDelete={handleDeviceDelete}
                      groupByLocation={true}
                      showOfflineDevices={filters.status !== 'online'}
                    />
                  </Suspense>
                )}
              </Box>
            </Grid>

            {/* Device Details Panel */}
            {selectedDevice && (
              <Grid item xs={12} md={5} sx={{ height: '100%', borderLeft: 1, borderColor: 'divider' }}>
                <Suspense fallback={<LoadingFallback />}>
                  <DeviceDetailsPanel
                    device={selectedDevice}
                    onClose={() => setSelectedDevice(null)}
                    onEdit={() => handleDeviceEdit(selectedDevice)}
                    onDelete={() => handleDeviceDelete(selectedDevice)}
                    onRefresh={handleRefresh}
                  />
                </Suspense>
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      {/* Dialogs */}
      <Suspense fallback={null}>
        <DeviceDialog
          open={dialogOpen}
          mode={dialogMode}
          device={deviceToEdit}
          onClose={() => setDialogOpen(false)}
          onSave={() => {
            setDialogOpen(false);
            DeviceService.invalidateCache();
            loadDevices(true);
          }}
        />

        <ConfirmDialog
          open={confirmDialogOpen}
          title="Delete Device"
          content={`Are you sure you want to delete "${deviceToDelete?.friendlyName || 'this device'}"?`}
          confirmText="Delete"
          confirmColor="error"
          onConfirm={confirmDeleteDevice}
          onCancel={() => {
            setConfirmDialogOpen(false);
            setDeviceToDelete(null);
          }}
        />
      </Suspense>
    </Box>
  );
};

export default DeviceManagementOptimized;